import type { RealtimeChannel } from "ably";
import { getChannel } from "./client";
import { ABLY_CONFIG, getRuntimeConfig } from "./config";
import type { PublishGuardState } from "./types";

export type AblyMessage = {
	name: string;
	data: unknown;
};

export type SafePublishOptions = {
	timeoutMs?: number;
	channelName?: string | null;
	channel?: RealtimeChannel | null;
	connectionGuard?: PublishGuardState;
	createConnectionError?: () => Error;
	createTimeoutError?: () => Error;
};

export function isTerminalChannelState(channel: RealtimeChannel): boolean {
	return channel.state === "failed" || channel.state === "suspended";
}

export function resolveChannel(options: Pick<SafePublishOptions, "channel" | "channelName">): RealtimeChannel {
	if (options.channel) {
		return options.channel;
	}

	if (!options.channelName) {
		throw new Error("Could not resolve Ably channel name");
	}

	return getChannel(options.channelName);
}

export function waitForAttach(
	channel: RealtimeChannel,
	timeoutMs: number,
	createAttachError?: () => Error,
): Promise<void> {
	if (channel.state === "attached") {
		return Promise.resolve();
	}

	if (isTerminalChannelState(channel)) {
		return Promise.reject(createAttachError ? createAttachError() : new Error(`Ably channel is ${channel.state}`));
	}

	return new Promise((resolve, reject) => {
		let settled = false;

		const cleanup = () => {
			if (settled) return;
			settled = true;
			clearTimeout(timer);
			channel.off(stateListener);
		};

		const stateListener = (stateChange: { current: string }) => {
			if (stateChange.current === "attached") {
				cleanup();
				resolve();
				return;
			}

			if (stateChange.current === "failed" || stateChange.current === "suspended") {
				cleanup();
				reject(
					createAttachError ? createAttachError() : new Error(`Ably channel transitioned to ${stateChange.current}`),
				);
			}
		};

		const timer = setTimeout(() => {
			cleanup();
			reject(createAttachError ? createAttachError() : new Error(`Ably channel did not attach within ${timeoutMs}ms`));
		}, timeoutMs);

		channel.on(stateListener);
	});
}

export async function publishWithRetry(channel: RealtimeChannel, message: AblyMessage): Promise<void> {
	const { logger, metrics } = getRuntimeConfig();
	const startedAt = Date.now();
	let lastError: Error | undefined;
	let attempts = 0;

	for (let attempt = 0; attempt <= ABLY_CONFIG.maxPublishRetries; attempt++) {
		attempts = attempt + 1;
		try {
			await channel.publish(message.name, message.data);
			metrics?.track({
				event: "realtime.publish",
				success: true,
				messageName: message.name,
				attemptCount: attempts,
				latencyMs: Date.now() - startedAt,
			});
			return;
		} catch (error) {
			lastError = error instanceof Error ? error : new Error(String(error));
			logger.warning("INFO", `Publish attempt ${attempt + 1} failed for ${message.name}`, lastError);

			if (attempt === ABLY_CONFIG.maxPublishRetries || isTerminalChannelState(channel)) {
				break;
			}

			await new Promise((resolve) => setTimeout(resolve, ABLY_CONFIG.publishRetryDelayMs));
		}
	}

	metrics?.track({
		event: "realtime.publish",
		success: false,
		messageName: message.name,
		attemptCount: attempts,
		latencyMs: Date.now() - startedAt,
		error: lastError?.message,
	});

	throw lastError ?? new Error(`Failed to publish ${message.name}`);
}

export async function safePublish(message: AblyMessage, options: SafePublishOptions = {}): Promise<void> {
	const {
		timeoutMs = ABLY_CONFIG.confirmSendTimeoutMs,
		connectionGuard,
		createConnectionError,
		createTimeoutError,
	} = options;

	if (connectionGuard?.isReady && !connectionGuard.isConnected) {
		throw createConnectionError ? createConnectionError() : new Error("Ably peer is not connected");
	}

	const channel = resolveChannel(options);
	await waitForAttach(channel, timeoutMs, createTimeoutError ?? createConnectionError);

	try {
		await publishWithRetry(channel, message);
	} catch (error) {
		if (isTerminalChannelState(channel) && createConnectionError) {
			throw createConnectionError();
		}

		throw error;
	}
}
